// src/components/admin_panel/AdminMeetupGroupFinance.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import SourceGroupMappingsNeedsReview from "./SourceGroupMappingsNeedsReview";

// Same green/red pairing as Landing Page Performance's Profit/Loss column -
// green for a group in profit, red for a loss - reused here rather than
// picked fresh so "green = good, red = bad" stays consistent across tabs.
const PROFIT_COLOR = "#10b981";
const LOSS_COLOR = "#ef4444";
// Distinct blue for the true-MRR recurring-revenue figure so it's visually
// distinguishable from the period-revenue figure (emerald) at a glance,
// not just by its column header text.
const RECURRING_COLOR = "#2563eb";
// Muted purple for the narrower "new signups this period, still active"
// figure - deliberately less prominent than RECURRING_COLOR's blue, since
// this is the smaller, more niche of the two recurring-revenue figures.
const NEW_RECURRING_COLOR = "#7c3aed";

// Fixed 8-hue categorical order (validated colorblind-safe as an ordered
// set - see the dataviz skill's reference palette), assigned in this exact
// order and never reassigned by sort/filter - a group's line color stays
// its own color across refreshes as long as it stays in the top-8-by-
// activity set (see MAX_INDIVIDUAL_GROUP_LINES below).
const GROUP_LINE_COLORS = [
  "#2a78d6", // blue
  "#eb6834", // orange
  "#1baf7a", // aqua
  "#eda100", // yellow
  "#e87ba4", // magenta
  "#008300", // green
  "#4a3aa7", // violet
  "#e34948", // red
];
// Beyond 8 simultaneous lines, an assigned hue can no longer be reliably
// told apart from another (colorblind-safe or not) - any groups past this
// count are folded into one "Other Groups" line instead of reusing/cycling
// a color, same as any other categorical chart with more identities than
// hues.
const MAX_INDIVIDUAL_GROUP_LINES = GROUP_LINE_COLORS.length;
const OTHER_GROUPS_COLOR = "#898781"; // muted ink - an aggregate, not a real identity
const TOTAL_LINE_COLOR = "#0b0b0b"; // primary ink - a reference line, not a peer category
const OTHER_GROUPS_KEY = "__other_groups__";
const TOTAL_KEY = "__total__";
// Same two pseudo-rows the table below shows (see build_meetup_group_finance's
// docstring for Unaffiliated vs Unattributed) - neither is a real Meetup
// group, so both get their own fixed muted colors outside the 8-hue
// categorical set rather than competing for one of those slots.
const UNAFFILIATED_KEY = "__unaffiliated__";
const UNATTRIBUTED_KEY = "__unattributed__";
const UNAFFILIATED_COLOR = "#9ca3af";
const UNATTRIBUTED_COLOR = "#4b5563";

const formatMonthLabel = (monthStr) => {
  const [year, month] = monthStr.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-GB", {
    month: "short", year: "numeric", timeZone: "UTC",
  });
};

// Same pattern as Landing Page Performance's TimeSeriesTooltip/ToggleLegend
// (not shared between the two files today) - series identity is carried by
// the colored dot + text label together, never color alone, and every
// value is plain readable text.
const ProfitTimeSeriesTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{
      background: "white", opacity: 1, border: "1px solid #e5e7eb", borderRadius: "8px",
      padding: "10px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", fontSize: "13px",
      maxWidth: "280px", position: "relative", zIndex: 10,
    }}>
      <div style={{ fontWeight: "600", marginBottom: "6px", color: "#111827" }}>{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "2px 0" }}>
          <span style={{
            width: "8px", height: "8px", borderRadius: "50%", background: entry.color, flexShrink: 0,
          }} />
          <span style={{ color: "#6b7280" }}>{entry.name}:</span>
          <span style={{
            fontWeight: "600", color: entry.value >= 0 ? "#111827" : LOSS_COLOR,
          }}>
            {formatGBP(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

// Eye-catching loading state for the chart card - a spinning ring plus an
// animated "shimmer" skeleton the same height the real chart will occupy,
// so an admin can immediately tell a graph is on its way (not just "the
// page is busy") and the card doesn't jump size once it arrives. This
// tab's other loading states are a plain gray "Loading..." line, which is
// fine for a table appearing/disappearing in place, but easy to miss for
// this card sitting prominently at the very top of the page - especially
// since real Stripe pagination (see MAX_CONCURRENT_STRIPE_MONTH_FETCHES in
// mixpanel_service.py) can take a few seconds, long enough that a subtle
// loading state reads as the page having silently failed to update.
const CHART_LOADING_KEYFRAMES = `
@keyframes profit-chart-spin { to { transform: rotate(360deg); } }
@keyframes profit-chart-shimmer { 0%, 100% { opacity: 0.35; } 50% { opacity: 0.9; } }
`;

const ChartLoadingState = () => (
  <div style={{ padding: "10px 0" }}>
    <style>{CHART_LOADING_KEYFRAMES}</style>
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: "14px", padding: "36px 20px 28px",
    }}>
      <div style={{
        width: "46px", height: "46px", borderRadius: "50%",
        border: "4px solid #e0e7ff", borderTopColor: "#667eea",
        animation: "profit-chart-spin 0.8s linear infinite",
      }} />
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#374151" }}>
          📈 Loading profit history…
        </div>
        <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px" }}>
          Pulling {PROFIT_HISTORY_MONTHS} months of Stripe revenue per group - this can take a few seconds
        </div>
      </div>
    </div>
    <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", height: "120px", padding: "0 16px" }}>
      {[52, 78, 40, 95, 64, 88].map((heightPct, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: `${heightPct}%`,
            borderRadius: "4px 4px 0 0",
            background: i % 2 === 0 ? "#c7d2fe" : "#a5b4fc",
            animation: `profit-chart-shimmer 1.3s ease-in-out ${i * 0.08}s infinite`,
          }}
        />
      ))}
    </div>
  </div>
);

// Solid background (not just transparent text floating over the chart) so
// that if this legend ever sits close to or overlaps plotted lines/axis
// labels - which a long, multi-row legend can on a narrow/mobile chart -
// its own text stays legible instead of visually blending with whatever
// is behind it.
const ProfitToggleLegend = ({ payload, hiddenSeries, onToggle }) => (
  <div style={{
    display: "flex", gap: "14px", flexWrap: "wrap", justifyContent: "center",
    marginTop: "6px", background: "white", position: "relative", zIndex: 2,
    padding: "4px 2px",
  }}>
    {(payload || []).map((entry) => {
      const isHidden = hiddenSeries.has(entry.dataKey);
      return (
        <button
          key={entry.dataKey}
          type="button"
          onClick={() => onToggle(entry.dataKey)}
          style={{
            display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none",
            cursor: "pointer", padding: "2px 4px", fontSize: "13px",
            color: isHidden ? "#9ca3af" : "#374151",
            textDecoration: isHidden ? "line-through" : "none",
          }}
        >
          <span style={{
            width: "10px", height: "10px", borderRadius: "50%", flexShrink: 0,
            background: isHidden ? "#d1d5db" : entry.color,
          }} />
          {entry.value}
        </button>
      );
    })}
  </div>
);

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

// Matches mixpanel_service.py's CACHE_TTL_SECONDS - this tab reads from the
// exact same server-side cached response as Landing Page Performance (same
// endpoint, same date range = same cache entry), so a request for a range
// already fetched recently (by either tab) costs no extra Mixpanel/Stripe
// calls at all.
const SERVER_CACHE_TTL_SECONDS = 15 * 60;
const SERVER_CACHE_MINUTES = SERVER_CACHE_TTL_SECONDS / 60;
const DATE_CHANGE_DEBOUNCE_MS = 500;

const MOBILE_BREAKPOINT_PX = 700;

// Shorter default window (the API supports up to 24 - see months= in
// app.py's get_meetup_group_profit_history) so the profit chart isn't
// cramped with a year of monthly points by default; the server has no
// separate cache entry cost either way, this is purely about what fits
// legibly on screen at once.
const PROFIT_HISTORY_MONTHS = 6;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT_PX
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

const formatAge = (seconds) => {
  if (seconds == null) return null;
  if (seconds < 60) return `${Math.round(seconds)}s ago`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
};

const StatRow = ({ label, value, valueStyle }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "baseline",
    padding: "6px 0", borderBottom: "1px solid #f3f4f6", fontSize: "13px", gap: "12px",
  }}>
    <span style={{ color: "#6b7280" }}>{label}</span>
    <span style={{ fontWeight: "600", textAlign: "right", ...valueStyle }}>{value}</span>
  </div>
);

// Small "i" affordance for explaining a term inline, e.g. Unaffiliated vs
// Unattributed - unlike a bare `title` attribute on the text itself, this
// gives a visible cue that there's an explanation to hover over.
const InfoIcon = ({ text }) => (
  <span
    title={text}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "14px",
      height: "14px",
      borderRadius: "50%",
      background: "#e5e7eb",
      color: "#6b7280",
      fontSize: "10px",
      fontStyle: "normal",
      fontWeight: "700",
      marginLeft: "5px",
      cursor: "help",
      flexShrink: 0,
    }}
  >
    i
  </span>
);

const UNAFFILIATED_EXPLANATION = "Subscriptions with a known page/source but no Meetup group tied to it";
const UNATTRIBUTED_EXPLANATION = "Real Stripe subscriptions we have no landing page or traffic source data for at all";

const MobileStatCard = ({ title, children }) => (
  <div style={{
    border: "1px solid #e5e7eb", borderRadius: "10px", padding: "12px 14px", marginBottom: "10px",
  }}>
    <div style={{ fontWeight: "600", marginBottom: "4px" }}>{title}</div>
    {children}
  </div>
);

const SummaryStatTile = ({ label, value, color, sublabel }) => (
  <div style={{
    background: "white", borderRadius: "12px", padding: "16px 18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  }}>
    <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px" }}>{label}</div>
    <div style={{ fontSize: "24px", fontWeight: "700", color: color || "#111827" }}>{value}</div>
    {sublabel && <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>{sublabel}</div>}
  </div>
);

const gbpFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
const formatGBP = (value) => gbpFormatter.format(value ?? 0);
// Narrower "£1.2k" form for the chart's Y-axis on mobile, where a full
// "£1,234.56" tick would eat most of the available chart width.
const gbpCompactFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'GBP', notation: 'compact', maximumFractionDigits: 1,
});
const formatGBPCompact = (value) => gbpCompactFormatter.format(value ?? 0);

const AdminMeetupGroupFinance = ({ adminToken }) => {
  const isMobile = useIsMobile();
  // Defaults to the last 4 weeks, matching how "revenue over the past 4
  // weeks" is framed on this tab - adjustable via the date pickers below
  // same as any other analytics tab.
  const [fromDate, setFromDate] = useState(daysAgoStr(28));
  const [toDate, setToDate] = useState(todayStr());
  const [sourceFilter, setSourceFilter] = useState("");
  const [availableSources, setAvailableSources] = useState([]);
  const [meetupGroupFinance, setMeetupGroupFinance] = useState(null);
  const [cacheMeta, setCacheMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cacheMetaReceivedAt, setCacheMetaReceivedAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  // Profit-over-time chart data - deliberately its own fetch, independent
  // of fromDate/toDate above (this always covers its own trailing window,
  // see mixpanel_service.get_meetup_group_profit_history), but it DOES
  // follow sourceFilter, the same way the table below does, so a group's
  // figures agree between the two rather than the chart silently always
  // summing every source regardless of what's selected.
  const [profitHistory, setProfitHistory] = useState(null);
  const [profitHistoryLoading, setProfitHistoryLoading] = useState(true);
  const [profitHistoryError, setProfitHistoryError] = useState("");
  // Unaffiliated/Unattributed start hidden (still toggleable, same as any
  // other line) - they're always in the data for Total's sake (see
  // chartSeries below) but aren't real groups, so they'd just be visual
  // noise by default on a chart that's otherwise one line per real group.
  const [hiddenSeries, setHiddenSeries] = useState(() => new Set([UNAFFILIATED_KEY, UNATTRIBUTED_KEY]));
  // A group with no payment in the trailing 4 weeks (see 'is_active' from
  // the API - mixpanel_service.ACTIVE_GROUP_WINDOW_DAYS) is hidden from the
  // chart by default and excluded from Total's cost, since it's no longer
  // a real ongoing cost to plot - this lets an admin opt back into seeing
  // it anyway.
  const [showInactiveGroups, setShowInactiveGroups] = useState(false);

  useEffect(() => {
    if (!adminToken) return;
    let cancelled = false;

    (async () => {
      setProfitHistoryLoading(true);
      setProfitHistoryError("");
      try {
        const params = new URLSearchParams({ months: String(PROFIT_HISTORY_MONTHS) });
        if (sourceFilter) params.set('source', sourceFilter);
        const response = await fetch(`/api/admin/analytics/meetup-group-profit-history?${params}`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || `HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) setProfitHistory(data);
      } catch (err) {
        console.error("Error fetching meetup group profit history:", err);
        if (!cancelled) setProfitHistoryError(err.message || "Failed to load profit history");
      } finally {
        if (!cancelled) setProfitHistoryLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [adminToken, sourceFilter]);

  const toggleSeries = useCallback((dataKey) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) next.delete(dataKey); else next.add(dataKey);
      return next;
    });
  }, []);

  // Up to MAX_INDIVIDUAL_GROUP_LINES groups get their own colored line,
  // picked by total |profit| magnitude across the window (the groups whose
  // trend actually matters most to look at) - any remainder is folded into
  // one "Other Groups" line rather than reusing/cycling a color past the
  // validated 8-hue categorical set (see GROUP_LINE_COLORS). Colors are
  // then assigned to the CHOSEN groups in alphabetical order (not rank
  // order), so a group's line color doesn't shuffle around just because
  // its relative rank shifted between two loads - only which groups make
  // the individually-colored cut can change.
  const chartSeries = useMemo(() => {
    const allEntries = profitHistory?.groups || [];
    // Unaffiliated (a known page/source but no configured Meetup group) and
    // Unattributed (no page/source data at all) - the same two pseudo-rows
    // build_meetup_group_finance's table shows, both associated_meetup_group:
    // null on the API. Neither is a real group to rank/color/fold like the
    // ones below, and neither has an 'is_active' concept that means
    // anything (the API always marks them active) - they're kept separate
    // and always included so Total reconciles with the table's Total.
    const unaffiliatedEntry = allEntries.find((g) => g.associated_meetup_group == null && !g.unattributed);
    const unattributedEntry = allEntries.find((g) => g.associated_meetup_group == null && g.unattributed);
    const realGroups = allEntries.filter((g) => g.associated_meetup_group != null);

    const inactiveCount = realGroups.filter((g) => !g.is_active).length;
    const groups = showInactiveGroups ? realGroups : realGroups.filter((g) => g.is_active);
    if (groups.length === 0 && !unaffiliatedEntry && !unattributedEntry) {
      return { series: [], chartData: [], otherGroupNames: [], inactiveCount };
    }

    const byMagnitude = [...groups].sort((a, b) => {
      const totalA = a.monthly.reduce((sum, m) => sum + Math.abs(m.profit), 0);
      const totalB = b.monthly.reduce((sum, m) => sum + Math.abs(m.profit), 0);
      return totalB - totalA;
    });
    const individualGroups = byMagnitude.slice(0, MAX_INDIVIDUAL_GROUP_LINES);
    const otherGroups = byMagnitude.slice(MAX_INDIVIDUAL_GROUP_LINES);

    const sortedAlphabetically = [...individualGroups].sort(
      (a, b) => a.associated_meetup_group.localeCompare(b.associated_meetup_group)
    );
    const series = sortedAlphabetically.map((g, i) => ({
      key: g.associated_meetup_group,
      label: g.associated_meetup_group,
      color: GROUP_LINE_COLORS[i],
    }));
    if (otherGroups.length > 0) {
      series.push({
        key: OTHER_GROUPS_KEY,
        label: `Other Groups (${otherGroups.length})`,
        color: OTHER_GROUPS_COLOR,
      });
    }
    if (unaffiliatedEntry) {
      series.push({ key: UNAFFILIATED_KEY, label: "Unaffiliated", color: UNAFFILIATED_COLOR });
    }
    if (unattributedEntry) {
      series.push({ key: UNATTRIBUTED_KEY, label: "Unattributed", color: UNATTRIBUTED_COLOR });
    }
    series.push({ key: TOTAL_KEY, label: "Total", color: TOTAL_LINE_COLOR });

    const months = profitHistory.months || [];
    const chartData = months.map((month, i) => {
      const point = { month: formatMonthLabel(month) };
      individualGroups.forEach((g) => { point[g.associated_meetup_group] = g.monthly[i].profit; });
      if (otherGroups.length > 0) {
        point[OTHER_GROUPS_KEY] = otherGroups.reduce((sum, g) => sum + g.monthly[i].profit, 0);
      }
      if (unaffiliatedEntry) point[UNAFFILIATED_KEY] = unaffiliatedEntry.monthly[i].profit;
      if (unattributedEntry) point[UNATTRIBUTED_KEY] = unattributedEntry.monthly[i].profit;
      point[TOTAL_KEY] = (profitHistory.total?.[i]?.profit) ?? 0;
      return point;
    });

    return { series, chartData, otherGroupNames: otherGroups.map((g) => g.associated_meetup_group), inactiveCount };
  }, [profitHistory, showInactiveGroups]);

  const fetchData = useCallback(async () => {
    if (!adminToken) return;

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      if (sourceFilter) params.set('source', sourceFilter);
      // Same endpoint Landing Page Performance uses - meetup_group_finance
      // is computed there for free by re-aggregating data already fetched
      // for the funnel, so calling it here for the same date range costs no
      // extra Mixpanel/Stripe query (server-side cache is shared by range).
      const response = await fetch(`/api/admin/analytics/landing-page-performance?${params}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMeetupGroupFinance(data.meetup_group_finance || null);
      setAvailableSources(data.available_sources || []);
      setCacheMeta(data.cache || null);
      setCacheMetaReceivedAt(Date.now());
    } catch (err) {
      console.error("Error fetching meetup group finance:", err);
      setError(err.message || "Failed to load Meetup Group Finance");
    } finally {
      setLoading(false);
    }
  }, [adminToken, fromDate, toDate, sourceFilter]);

  useEffect(() => {
    if (!adminToken) return;
    const timer = setTimeout(() => { fetchData(); }, DATE_CHANGE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, fromDate, toDate, sourceFilter]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const liveAgeSeconds = (cacheMeta?.funnel && cacheMetaReceivedAt)
    ? cacheMeta.funnel.age_seconds + (nowTick - cacheMetaReceivedAt) / 1000
    : null;
  const isServingStaleFallback = !!cacheMeta?.funnel?.stale;
  const canForceRefresh = isServingStaleFallback || liveAgeSeconds == null || liveAgeSeconds >= SERVER_CACHE_TTL_SECONDS;
  const refreshCooldownRemainingSeconds = liveAgeSeconds == null ? 0 : Math.max(SERVER_CACHE_TTL_SECONDS - liveAgeSeconds, 0);

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ marginBottom: "10px", color: "#111827" }}>
        💰 Meetup Group Finance
      </h2>

      <div style={{
        background: "white", borderRadius: "12px", padding: "20px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)", marginBottom: "20px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: "8px" }}>
          <h3 style={{ margin: 0, fontSize: "16px", color: "#111827" }}>Profit Over Time</h3>
          <span style={{ fontSize: "12px", color: "#9ca3af" }}>
            Trailing {PROFIT_HISTORY_MONTHS} months, one line per Meetup group
          </span>
        </div>
        <p style={{ color: "#6b7280", fontSize: "12px", margin: "6px 0 14px" }}>
          Same figures as the table below, just bucketed by calendar month instead of one selected range,
          and following whatever Traffic Source is currently selected there. Revenue is real money
          collected each month. Cost is {formatGBP(50)}/month per group, prorated down to only the days a
          group was actually live that month - whether that's because it just went live partway through
          (based on the earliest of: its landing page's creation date, its earliest synced Meetup event,
          or its earliest synced RSVP history), or because the month itself is still in progress, so its
          revenue reflects only what's been collected so far too. A group with no payment in the last 4
          weeks is treated as wound down: its line is hidden below by default and its cost is left out of
          Total (its revenue isn't - real money already collected still counts). "Unaffiliated"/
          "Unattributed" (see below) are included in Total but hidden from the chart by default too.
        </p>

        {isMobile && (
          <div style={{
            display: "flex", alignItems: "center", gap: "8px",
            background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: "8px",
            padding: "8px 12px", marginBottom: "12px", fontSize: "12px", color: "#3730a3",
          }}>
            <span style={{ fontSize: "16px" }}>📱↻</span>
            Tip: rotate your phone to landscape for a clearer view of this graph
          </div>
        )}

        {!profitHistoryLoading && !profitHistoryError && chartSeries.inactiveCount > 0 && (
          <label style={{
            display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#6b7280",
            marginBottom: "10px", cursor: "pointer", userSelect: "none",
          }}>
            <input
              type="checkbox"
              checked={showInactiveGroups}
              onChange={(e) => setShowInactiveGroups(e.target.checked)}
            />
            Show {chartSeries.inactiveCount} inactive group{chartSeries.inactiveCount === 1 ? '' : 's'} too
            (no payment in the last 4 weeks)
          </label>
        )}

        {profitHistoryLoading && <ChartLoadingState />}

        {!profitHistoryLoading && profitHistoryError && (
          <div style={{
            background: "#fee2e2", border: "1px solid #ef4444", borderRadius: "8px",
            padding: "12px 16px", color: "#991b1b", fontSize: "13px",
          }}>
            {profitHistoryError}
          </div>
        )}

        {!profitHistoryLoading && !profitHistoryError && chartSeries.series.length === 0 && (
          <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>
            No Meetup groups with revenue history yet
          </div>
        )}

        {!profitHistoryLoading && !profitHistoryError && chartSeries.series.length > 0 && (
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 340}>
            <LineChart
              data={chartSeries.chartData}
              margin={isMobile ? { top: 4, right: 8, left: 4, bottom: 28 } : { top: 4, right: 12, left: 8, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: isMobile ? 10 : 12, fill: "#6b7280" }}
                interval={isMobile ? 1 : 0}
                angle={isMobile ? -45 : 0}
                textAnchor={isMobile ? "end" : "middle"}
                height={isMobile ? 40 : 30}
              />
              <YAxis
                width={isMobile ? 48 : 64}
                tick={{ fontSize: isMobile ? 10 : 12, fill: "#6b7280" }}
                tickFormatter={(v) => (isMobile ? formatGBPCompact(v) : formatGBP(v))}
              />
              <Tooltip content={<ProfitTimeSeriesTooltip />} wrapperStyle={{ zIndex: 10, outline: "none" }} />
              {chartSeries.series.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  hide={hiddenSeries.has(s.key)}
                  stroke={s.color}
                  strokeWidth={s.key === TOTAL_KEY ? 3 : 2}
                  strokeDasharray={s.key === TOTAL_KEY ? "6 3" : undefined}
                  dot={{ r: isMobile ? 2 : 3 }}
                  activeDot={{ r: isMobile ? 4 : 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}

        {!profitHistoryLoading && !profitHistoryError && chartSeries.series.length > 0 && (
          // Rendered as plain HTML below the chart, NOT via Recharts' own
          // <Legend> inside the fixed-height chart above - with up to 11
          // possible entries (8 groups + Other + Unaffiliated + Unattributed
          // + Total), a Recharts-managed legend wraps to several rows on a
          // narrow mobile screen but Recharts still only reserves space for
          // roughly one row, so the wrapped rows ate into (and on the
          // narrowest phones, completely covered) the actual plotted lines.
          // Living outside the chart's fixed height, this can wrap to
          // however many rows it needs without stealing plot space.
          <ProfitToggleLegend
            payload={chartSeries.series.map((s) => ({ dataKey: s.key, value: s.label, color: s.color }))}
            hiddenSeries={hiddenSeries}
            onToggle={toggleSeries}
          />
        )}
      </div>

      <p style={{ color: "#666", marginBottom: "12px", fontSize: "14px" }}>
        Revenue/cost/profit per Meetup group. <strong>Revenue is real</strong>: "Revenue (this
        period)" sums every actual Stripe payment collected in the selected date range (GBP
        invoices only, since checkout only ever creates GBP subscriptions, so this covers the
        normal case), <strong>net of an estimated {formatGBP(0.30)} Stripe processing fee per
        payment</strong>, the same way Stripe's own dashboard reports revenue for a period. This
        is what the business actually keeps from money that actually came in, not the gross amount
        billed, and it includes renewal payments from subscribers who started well before this
        date range, not just brand-new signups. "Recurring Revenue" and "New Recurring Revenue
        (this period)" are two different, forward-looking figures built the same way from each
        subscriber's current price rather than actual payments (see below for how they differ).
        <strong>Cost is still an estimate</strong>: {formatGBP(50)} per Meetup group per month, prorated
        to the days actually covered by the selected date range (and to a group's own go-live date, if
        that falls inside it), since there's no record of what actually running a given group costs - a
        7-day range shows a small fraction of a month's cost, a 90-day range roughly three months' worth.
        A group with no payment in the last 4 weeks is treated as wound down and shows {formatGBP(0)} cost
        here regardless of the range selected (its revenue, if any, still counts). "Unaffiliated" covers
        subscriptions/payments that we know the page/source for but have no group at all, and
        "Unattributed" covers ones we have no landing page or source data for at all. Either way, there's
        no group to charge a cost against, so both always show {formatGBP(0)} cost.
      </p>

      <p style={{
        color: "#374151",
        marginBottom: "20px",
        fontSize: "13px",
        background: "#fffbeb",
        border: "1px solid #fde68a",
        borderRadius: "8px",
        padding: "10px 14px",
      }}>
        📍 Grouped by <strong>Associated Meetup Group</strong> — each subscription's own referral
        link (<code>?src=...</code>) when one was captured on their very first visit, otherwise the
        real-world Meetup group tied to the landing page they actually subscribed through. The
        referral link wins when both are available (it's a directly tracked click, not an
        inference), but falling back to the landing page's group when there's no referral link
        means most subscriptions still land under a real group here, rather than Unaffiliated.
      </p>

      <SourceGroupMappingsNeedsReview adminToken={adminToken} />

      <div style={{
        color: "#3730a3",
        marginBottom: "20px",
        fontSize: "13px",
        background: "#eef2ff",
        border: "1px solid #c7d2fe",
        borderRadius: "8px",
        padding: "12px 16px",
      }}>
        <strong>Three different revenue figures, on purpose, from three different populations:</strong>
        <ul style={{ margin: "6px 0 0", paddingLeft: "20px" }}>
          <li style={{ marginBottom: "4px" }}>
            <strong style={{ color: "#059669" }}>Revenue (this period)</strong>: every Stripe payment
            actually <strong>collected</strong> in the selected date range, from any subscriber
            regardless of when they originally signed up. This is the figure that should match
            Stripe's own dashboard for the same range. It's usually mostly renewal payments from
            existing subscribers, not new signups.
          </li>
          <li style={{ marginBottom: "4px" }}>
            <strong style={{ color: RECURRING_COLOR }}>Recurring Revenue</strong>: true current MRR,
            every subscriber who is <strong>currently active and NOT due to cancel</strong>,
            regardless of when they signed up. Unlike the other two figures, this one ignores the
            selected date range entirely (it's always "right now"), so it's the one that should
            match Stripe's own MRR/active-subscriber view.
          </li>
          <li>
            <strong style={{ color: NEW_RECURRING_COLOR }}>New Recurring Revenue (this period)</strong>:
            a narrower figure. Only subscribers who <strong>started in this date range</strong> and
            are <strong>currently active and NOT due to cancel</strong>. This is the ongoing revenue
            this period's new signups specifically add going forward, not the group's whole active
            base, and it isn't meant to reconcile with Stripe's dashboard the way the other two are.
          </li>
        </ul>
      </div>

      <p style={{
        color: "#6b7280",
        marginBottom: "20px",
        fontSize: "13px",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        padding: "10px 14px",
      }}>
        ℹ️ Changing the date range fetches automatically. Mixpanel limits how many queries this
        page can make, so results for a given range are cached for up to {SERVER_CACHE_MINUTES}{" "}
        minutes — Refresh is only enabled once that window has passed, since clicking it sooner
        would just return the same numbers.
      </p>

      <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", marginBottom: "20px", flexWrap: "wrap" }}>
        <div>
          <label style={{ display: "block", fontSize: "13px", color: "#374151", marginBottom: "4px" }}>From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "13px", color: "#374151", marginBottom: "4px" }}>To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "13px", color: "#374151", marginBottom: "4px" }}>Traffic Source</label>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", minWidth: "160px" }}
          >
            <option value="">All Sources</option>
            {availableSources.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={fetchData}
          disabled={loading || !canForceRefresh}
          title={!canForceRefresh
            ? `This data is still fresh (cached for up to ${SERVER_CACHE_MINUTES} minutes) - refreshing again won't return anything new for another ${Math.ceil(refreshCooldownRemainingSeconds / 60)}m`
            : undefined}
          style={{
            background: (loading || !canForceRefresh) ? "#a5b4fc" : "#667eea",
            color: "white",
            border: "none",
            padding: "9px 20px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: (loading || !canForceRefresh) ? "not-allowed" : "pointer",
          }}
        >
          Refresh
        </button>
        {cacheMeta?.funnel && (
          <span style={{ fontSize: "13px", color: "#9ca3af" }}>
            Data as of {formatAge(liveAgeSeconds)}
            {!canForceRefresh && ` · fresh for another ${Math.ceil(refreshCooldownRemainingSeconds / 60)}m`}
          </span>
        )}
      </div>

      {cacheMeta?.funnel?.stale && (
        <div style={{
          background: "#fef3c7",
          border: "1px solid #f59e0b",
          borderRadius: "8px",
          padding: "12px 16px",
          color: "#92400e",
          fontSize: "13px",
          marginBottom: "20px",
        }}>
          ⚠️ Mixpanel is rate-limiting requests right now, so this is showing the last successfully
          fetched data ({formatAge(cacheMeta.funnel.age_seconds)}) instead of failing. It'll refresh
          automatically once Mixpanel allows it again.
        </div>
      )}

      {loading && (
        <div style={{ padding: "40px", textAlign: "center", fontSize: "18px", color: "#666" }}>
          Loading...
        </div>
      )}

      {!loading && error && (
        <div style={{
          background: "#fee2e2",
          border: "1px solid #ef4444",
          borderRadius: "8px",
          padding: "16px",
          color: "#991b1b"
        }}>
          {error}
        </div>
      )}

      {!loading && !error && (!meetupGroupFinance || meetupGroupFinance.groups?.length === 0) && (
        <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
          No data for this date range
          {sourceFilter && ` and source (${availableSources.find(s => s.value === sourceFilter)?.label || sourceFilter})`}
        </div>
      )}

      {!loading && !error && meetupGroupFinance && meetupGroupFinance.groups?.length > 0 && (
        <div>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px",
            marginBottom: "28px",
          }}>
            <SummaryStatTile
              label="Revenue (this period)"
              value={formatGBP(meetupGroupFinance.total.revenue)}
              color="#059669"
              sublabel="Actually collected in range, any signup date"
            />
            <SummaryStatTile
              label="Recurring Revenue"
              value={formatGBP(meetupGroupFinance.total.recurring_revenue)}
              color={RECURRING_COLOR}
              sublabel="Current MRR, any signup date"
            />
            <SummaryStatTile
              label="New Recurring Revenue (this period)"
              value={formatGBP(meetupGroupFinance.total.new_recurring_revenue)}
              color={NEW_RECURRING_COLOR}
              sublabel="New signups this range, still active"
            />
            <SummaryStatTile label="Cost" value={formatGBP(meetupGroupFinance.total.cost)} />
            <SummaryStatTile
              label="Profit / Loss (this period)"
              value={formatGBP(meetupGroupFinance.total.profit)}
              color={meetupGroupFinance.total.profit >= 0 ? PROFIT_COLOR : LOSS_COLOR}
            />
          </div>

          {isMobile ? (
            <div>
              {meetupGroupFinance.groups.map((g) => (
                <MobileStatCard
                  key={g.associated_meetup_group || (g.unattributed ? 'unattributed' : 'unaffiliated')}
                  title={g.associated_meetup_group || (
                    <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                      {g.unattributed ? 'Unattributed' : 'Unaffiliated'}
                      <InfoIcon text={g.unattributed ? UNATTRIBUTED_EXPLANATION : UNAFFILIATED_EXPLANATION} />
                    </span>
                  )}
                >
                  {g.theme_names?.length > 0 && (
                    <StatRow label="Theme(s)" value={g.theme_names.join(', ')} />
                  )}
                  <StatRow label="Active (NOT due to cancel)" value={g.active_subscriptions} />
                  <StatRow label="New Subscriptions (period)" value={g.subscriptions} />
                  <StatRow label="Recurring Revenue" value={formatGBP(g.recurring_revenue)} valueStyle={{ color: RECURRING_COLOR }} />
                  <StatRow label="New Recurring Revenue (period)" value={formatGBP(g.new_recurring_revenue)} valueStyle={{ color: NEW_RECURRING_COLOR }} />
                  <StatRow label="Revenue (period)" value={formatGBP(g.revenue)} />
                  <StatRow label="Cost" value={formatGBP(g.cost)} />
                  <StatRow
                    label="Profit / Loss" value={formatGBP(g.profit)}
                    valueStyle={{ color: g.profit >= 0 ? PROFIT_COLOR : LOSS_COLOR }}
                  />
                </MobileStatCard>
              ))}
              <MobileStatCard title="Total">
                <StatRow label="Active (NOT due to cancel)" value={meetupGroupFinance.total.active_subscriptions} />
                <StatRow label="New Subscriptions (period)" value={meetupGroupFinance.total.subscriptions} />
                <StatRow label="Recurring Revenue" value={formatGBP(meetupGroupFinance.total.recurring_revenue)} valueStyle={{ color: RECURRING_COLOR }} />
                <StatRow label="New Recurring Revenue (period)" value={formatGBP(meetupGroupFinance.total.new_recurring_revenue)} valueStyle={{ color: NEW_RECURRING_COLOR }} />
                <StatRow label="Revenue (period)" value={formatGBP(meetupGroupFinance.total.revenue)} />
                <StatRow label="Cost" value={formatGBP(meetupGroupFinance.total.cost)} />
                <StatRow
                  label="Profit / Loss" value={formatGBP(meetupGroupFinance.total.profit)}
                  valueStyle={{ color: meetupGroupFinance.total.profit >= 0 ? PROFIT_COLOR : LOSS_COLOR }}
                />
              </MobileStatCard>
            </div>
          ) : (
            <div style={{
              background: "white", borderRadius: "12px", padding: "20px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)", overflowX: "auto",
            }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", minWidth: "1080px" }}>
                <thead>
                  <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600" }}>Associated Meetup Group</th>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600" }}>Theme(s)</th>
                    <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>Active (NOT due to cancel)</th>
                    <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>New Subscriptions (period)</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: "600", color: RECURRING_COLOR }}>Recurring Revenue</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: "600", color: NEW_RECURRING_COLOR }}>New Recurring Revenue (period)</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: "600" }}>Revenue (period)</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: "600" }}>Cost</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: "600" }}>Profit / Loss</th>
                  </tr>
                </thead>
                <tbody>
                  {meetupGroupFinance.groups.map((g) => (
                    <tr key={g.associated_meetup_group || (g.unattributed ? 'unattributed' : 'unaffiliated')} style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "10px 12px", fontWeight: "500", whiteSpace: "nowrap" }}>
                        {g.associated_meetup_group || (
                          <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                            {g.unattributed ? 'Unattributed' : 'Unaffiliated'}
                            <InfoIcon text={g.unattributed ? UNATTRIBUTED_EXPLANATION : UNAFFILIATED_EXPLANATION} />
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#6b7280" }}>
                        {g.theme_names?.length > 0 ? g.theme_names.join(', ') : '—'}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "center" }}>{g.active_subscriptions}</td>
                      <td style={{ padding: "10px 12px", textAlign: "center" }}>{g.subscriptions}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "600", color: RECURRING_COLOR }}>
                        {formatGBP(g.recurring_revenue)}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "600", color: NEW_RECURRING_COLOR }}>
                        {formatGBP(g.new_recurring_revenue)}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>{formatGBP(g.revenue)}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>{formatGBP(g.cost)}</td>
                      <td style={{
                        padding: "10px 12px", textAlign: "right", fontWeight: "600",
                        color: g.profit >= 0 ? PROFIT_COLOR : LOSS_COLOR,
                      }}>
                        {formatGBP(g.profit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ padding: "10px 12px", fontWeight: "700", borderTop: "2px solid #e5e7eb" }}>Total</td>
                    <td style={{ borderTop: "2px solid #e5e7eb" }}></td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: "700", borderTop: "2px solid #e5e7eb" }}>
                      {meetupGroupFinance.total.active_subscriptions}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: "700", borderTop: "2px solid #e5e7eb" }}>
                      {meetupGroupFinance.total.subscriptions}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "700", borderTop: "2px solid #e5e7eb", color: RECURRING_COLOR }}>
                      {formatGBP(meetupGroupFinance.total.recurring_revenue)}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "700", borderTop: "2px solid #e5e7eb", color: NEW_RECURRING_COLOR }}>
                      {formatGBP(meetupGroupFinance.total.new_recurring_revenue)}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "700", borderTop: "2px solid #e5e7eb" }}>
                      {formatGBP(meetupGroupFinance.total.revenue)}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "700", borderTop: "2px solid #e5e7eb" }}>
                      {formatGBP(meetupGroupFinance.total.cost)}
                    </td>
                    <td style={{
                      padding: "10px 12px", textAlign: "right", fontWeight: "700", borderTop: "2px solid #e5e7eb",
                      color: meetupGroupFinance.total.profit >= 0 ? PROFIT_COLOR : LOSS_COLOR,
                    }}>
                      {formatGBP(meetupGroupFinance.total.profit)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminMeetupGroupFinance;
